/**
 * Функции для поиска и навигации по элементам документа
 */

import { getVisibleLength } from "lib/text/text-content";
import { parseXpointer } from "lib/text/xpointer-range";
import type {
  Chapter,
  ContentNode,
  Index,
  Location,
  NavigationBookmark,
  Page,
  Part,
  RenderingContext,
  Xpointer,
} from "lib/types";

/**
 * Сериализует xpath в строку
 * @example [1, 2, 3] -> "/1/2/3.0"
 */
export function serializeXpath(xpath: number[]): string {
  return `/${xpath.join("/")}.0`;
}

/**
 * Парсит xpath из строки в массив чисел
 * @example "/1/2/3.0" -> [1, 2, 3]
 * @example "1/2/3.0" -> [1, 2, 3]
 */
export function parseXpath(xpath: string): number[] {
  // Убираем суффикс charIndex если передан полный xpointer (/1/4/63.0/1.5 → /1/4/63.0)
  const stripped = xpath.replace(/\/\d+\.(?!0$)\d+$/, "");
  const normalized = stripped.startsWith("/") ? stripped.slice(1) : stripped;
  return normalized.split("/").map(Number);
}

/**
 * Сравнивает два xpath посегментно
 * @returns -1 если a < b, 0 если a === b, 1 если a > b
 */
export function compareXpath(a: number[], b: number[]): -1 | 0 | 1 {
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const aVal = a[i] ?? 0;
    const bVal = b[i] ?? 0;
    if (aVal < bVal) return -1;
    if (aVal > bVal) return 1;
  }
  return 0;
}

/**
 * Проверяет, находится ли xpath внутри части (между xps и xpe)
 */
export function isXpathInPart(xpath: number[], part: Part): boolean {
  const cmpStart = compareXpath(xpath, part.xps);
  const cmpEnd = compareXpath(xpath, part.xpe);
  return cmpStart >= 0 && cmpEnd <= 0;
}

/**
 * Проверяет, является ли prefix префиксом full
 * Например [1, 1] является префиксом [1, 1, 1] — контейнер содержит потомка
 */
function isXpathPrefix(prefix: number[], full: number[]): boolean {
  if (prefix.length > full.length) return false;
  return prefix.every((val, i) => val === full[i]);
}

/**
 * Находит индекс части, содержащей данный xpath.
 * Если xpath — контейнер (префикс xps части), считаем что он принадлежит этой части.
 * Если xpath перед первой частью, возвращаем 0.
 */
export function partIndexByXpath(parts: Part[], xpath: number[]): number {
  const index = parts.findIndex(part => isXpathInPart(xpath, part));
  if (index !== -1) return index;
  const prefixIndex = parts.findIndex(part => isXpathPrefix(xpath, part.xps));
  if (prefixIndex !== -1) return prefixIndex;
  if (parts.length > 0 && compareXpath(xpath, parts[0].xps) < 0) return 0;
  return -1;
}

export function indexToContentNode(
  index: Index,
  context: RenderingContext
): ContentNode | null {
  const tocPartIndex = context.toc.Parts.findIndex(
    p => index >= p.s && index <= p.e
  );
  if (tocPartIndex === -1) return null;

  const part = context.toc.Parts[tocPartIndex];
  const localPartIdx = tocPartIndex - context.partsStartIndex;

  if (localPartIdx < 0 || localPartIdx >= context.parts.length) {
    return null;
  }

  const content = context.parts[localPartIdx];
  if (!content) return null;

  const arrayPosition = index - part.s;
  if (arrayPosition < 0 || arrayPosition >= content.length) {
    return null;
  }

  const node = content[arrayPosition];
  if (typeof node === "string") {
    return null;
  }

  return node;
}

export function xpointerToContentNode(
  xpointer: Xpointer,
  context: RenderingContext
): ContentNode | null {
  const index = xpointerToIndex(xpointer, context);
  if (index === null) return null;
  return indexToContentNode(index, context);
}

export function indexToXpointer(
  index: Index,
  context: RenderingContext
): Xpointer | null {
  const node = indexToContentNode(index, context);
  if (!node) return null;
  return serializeXpath(node.xp);
}

export function partByXpointer(parts: Part[], xpointer: Xpointer): Part | null {
  const xpath = parseXpath(xpointer);
  const index = partIndexByXpath(parts, xpath);
  return index !== -1 ? parts[index] : null;
}

export function xpointerToIndex(
  xpointer: Xpointer,
  context: RenderingContext
): Index | null {
  // Нормализуем xpointer до уровня элемента:
  // - текстовый узел: "/1/3/6/4/0.0" → "/1/3/6/4.0"
  // - charIndex: "/1/13/39.0/1.135" → "/1/13/39.0"
  const matchTextNode = xpointer.match(/^(.+)\/0\.\d+$/);
  let normalizedXpointer = matchTextNode ? `${matchTextNode[1]}.0` : xpointer;
  const matchCharIndex = normalizedXpointer.match(/^(.+\.0)\/\d+\.\d+$/);
  if (matchCharIndex) {
    normalizedXpointer = matchCharIndex[1];
  }

  // O(1) lookup из предвычисленной карты
  const indexMap = context.layout?.xpointerToIndex;
  if (indexMap) {
    const index = indexMap.get(normalizedXpointer);
    if (index !== undefined) {
      return index;
    }

    // Fallback: пробуем укороченные префиксы
    const segments = parseXpath(normalizedXpointer);
    for (let len = segments.length - 1; len >= 2; len--) {
      const prefixXpointer = serializeXpath(segments.slice(0, len));
      const prefixIndex = indexMap.get(prefixXpointer);
      if (prefixIndex !== undefined) {
        return prefixIndex;
      }
    }
  }

  // Fallback для случаев без layout (тесты, начальная загрузка)
  return xpointerToIndexSlow(normalizedXpointer, context);
}

function xpointerToIndexSlow(
  normalizedXpointer: string,
  context: RenderingContext
): Index | null {
  function findExact(targetXpointer: string): Index | null {
    function findInNode(node: ContentNode): boolean {
      if (serializeXpath(node.xp) === targetXpointer) return true;
      if (node.c) {
        for (const child of node.c) {
          if (typeof child !== "string" && findInNode(child)) return true;
        }
      }
      return false;
    }

    for (let partIdx = 0; partIdx < context.parts.length; partIdx++) {
      const content = context.parts[partIdx];
      if (!content) continue;

      for (let i = 0; i < content.length; i++) {
        const node = content[i];
        if (typeof node === "string") continue;

        const tocPartIndex = context.partsStartIndex + partIdx;
        const tocPart = context.toc.Parts[tocPartIndex];
        if (!tocPart) return null;

        if (findInNode(node)) {
          return tocPart.s + i;
        }
      }
    }
    return null;
  }

  function findByPrefix(prefixSegments: number[]): Index | null {
    function nodeStartsWith(node: ContentNode): boolean {
      if (node.xp.length < prefixSegments.length) return false;
      for (let i = 0; i < prefixSegments.length; i++) {
        if (node.xp[i] !== prefixSegments[i]) return false;
      }
      return true;
    }

    for (let partIdx = 0; partIdx < context.parts.length; partIdx++) {
      const content = context.parts[partIdx];
      if (!content) continue;

      for (let i = 0; i < content.length; i++) {
        const node = content[i];
        if (typeof node === "string") continue;

        const tocPartIndex = context.partsStartIndex + partIdx;
        const tocPart = context.toc.Parts[tocPartIndex];
        if (!tocPart) return null;

        if (nodeStartsWith(node)) {
          return tocPart.s + i;
        }
      }
    }
    return null;
  }

  const exact = findExact(normalizedXpointer);
  if (exact !== null) return exact;

  const segments = parseXpath(normalizedXpointer);
  for (let len = segments.length - 1; len >= 2; len--) {
    const prefixSegments = segments.slice(0, len);
    const exactResult = findExact(serializeXpath(prefixSegments));
    if (exactResult !== null) return exactResult;
    const prefixResult = findByPrefix(prefixSegments);
    if (prefixResult !== null) return prefixResult;
  }

  return null;
}

/**
 * Находит главу по порядковому номеру (1-based) в плоском списке оглавления.
 * Для order за пределами диапазона возвращает последнюю главу.
 */
export function chapterByOrder(
  toc: { Body?: { c?: Chapter[] }[] },
  order: number
): Chapter | null {
  const chapters = toc.Body?.[0]?.c;
  if (!chapters || order < 1) return null;

  let count = 0;
  let last: Chapter | null = null;

  function find(items: Chapter[]): Chapter | null {
    for (const item of items) {
      if (item.t) {
        last = item;
        if (++count === order) return item;
      }
      if (item.c) {
        const found = find(item.c);
        if (found) return found;
      }
    }
    return null;
  }

  return find(chapters) ?? last;
}

/**
 * Находит самую глубокую главу, содержащую данный xpointer.
 */
export function xpointerToChapter(
  xpointer: Xpointer,
  context: RenderingContext
): Chapter | null {
  if (!context.toc?.Body) return null;

  const dataIndex = xpointerToIndex(xpointer, context);
  if (dataIndex === null) return null;

  function find(chapters: Chapter[], parent: Chapter | null): Chapter | null {
    for (const chapter of chapters) {
      if (chapter.s <= dataIndex && chapter.e >= dataIndex) {
        if (chapter.c) {
          const found = find(chapter.c, chapter.t ? chapter : parent);
          if (found) return found;
        }
        return chapter.t ? chapter : parent;
      }
    }
    return parent;
  }

  return find(context.toc.Body, null);
}

/**
 * Возвращает порядковый номер (1-based) главы в плоском списке оглавления.
 */
export function orderByChapter(
  target: Chapter,
  toc: { Body?: { c?: Chapter[] }[] }
): number | null {
  const chapters = toc.Body?.[0]?.c;
  if (!chapters) return null;

  let count = 0;
  function find(items: Chapter[]): number | null {
    for (const item of items) {
      if (item.t) {
        count++;
        if (item === target) return count;
      }
      if (item.c) {
        const found = find(item.c);
        if (found) return found;
      }
    }
    return null;
  }

  return find(chapters);
}

export function xpointerToLocation(
  xpointer: Xpointer,
  context: RenderingContext
): Location | null {
  const index = xpointerToIndex(xpointer, context);
  if (index === null || !context.layout) return null;

  let location = context.layout.elements[index];

  if (!location) {
    const elementIndices = Object.keys(context.layout.elements)
      .map(k => parseInt(k, 10))
      .sort((a, b) => a - b);

    let closestIndex: number | null = null;
    for (const elementIndex of elementIndices) {
      if (elementIndex <= index) {
        closestIndex = elementIndex;
      } else {
        break;
      }
    }

    if (closestIndex !== null) {
      location = context.layout.elements[closestIndex];
    }
  }

  if (!location) return null;

  return location;
}

/**
 * Интерполирует страницу по charIndex внутри диапазона страниц элемента.
 */
function interpolatePage(
  charIndex: number,
  visibleLength: number,
  location: Location
): Page {
  const ratio = Math.min(charIndex / visibleLength, 1);
  const pageSpan = location.endPage - location.startPage;
  const targetPage = location.startPage + Math.round(ratio * pageSpan);
  return Math.min(targetPage, location.endPage);
}

/**
 * Определяет страницу по xpointer с учётом смещения символа.
 * Если элемент занимает несколько страниц и xpointer содержит charIndex,
 * интерполирует позицию внутри диапазона страниц элемента.
 */
export function xpointerToPage(
  xpointer: Xpointer,
  context: RenderingContext
): Page | null {
  const location = xpointerToLocation(xpointer, context);
  if (!location) return null;

  const parsed = parseXpointer(xpointer);
  const charIndex = parsed?.charIndex ?? 0;
  const index = xpointerToIndex(xpointer, context);

  // Если charIndex > 0, проверяем что он вписывается в элемент
  if (charIndex > 0 && index !== null) {
    const node = indexToContentNode(index, context);
    const visibleLength = node ? getVisibleLength(node) : 0;

    // charIndex выходит за пределы текста элемента — xpointer скорее всего относится к предыдущему элементу
    if (node && charIndex > visibleLength && context.layout) {
      const prevLocation = context.layout.elements[index - 1];
      const prevNode = indexToContentNode(index - 1, context);
      const prevVisibleLength = prevNode ? getVisibleLength(prevNode) : 0;

      if (prevLocation && prevNode && charIndex <= prevVisibleLength) {
        return interpolatePage(charIndex, prevVisibleLength, prevLocation);
      }
    }

    // Стандартная интерполяция для многостраничных элементов
    if (location.startPage !== location.endPage && node && visibleLength > 0) {
      return interpolatePage(charIndex, visibleLength, location);
    }
  }

  return location.startPage;
}

export function pageToXpointer(
  page: Page,
  context: RenderingContext
): Xpointer | null {
  const bookmark = pageToBookmark(page, context);
  return bookmark?.xpointer ?? null;
}

export function pageToBookmark(
  page: Page,
  context: RenderingContext
): NavigationBookmark | null {
  if (!context.layout) return null;
  const indices = Object.keys(context.layout.elements)
    .map(k => parseInt(k, 10))
    .sort(
      (a, b) =>
        context.layout.elements[a].startPage -
        context.layout.elements[b].startPage
    );

  if (indices.length === 0) return null;

  const totalPages = context.layout.totalPages;
  const percent = (page / totalPages) * 100;

  // Находим первый элемент, который начинается на указанной странице
  for (const index of indices) {
    const location = context.layout.elements[index];
    if (location.startPage === page) {
      const xpointer = indexToXpointer(index, context);
      if (xpointer) {
        return { xpointer, percent };
      }
    }
  }

  // Если нет элемента начинающегося на этой странице,
  // ищем элемент который охватывает эту страницу (startPage < page <= endPage)
  for (const index of indices) {
    const location = context.layout.elements[index];
    if (location.startPage < page && location.endPage >= page) {
      const xpointer = indexToXpointer(index, context);
      if (xpointer) {
        return { xpointer, percent };
      }
    }
  }

  // Fallback: первый элемент после этой страницы
  for (const index of indices) {
    const location = context.layout.elements[index];
    if (location.startPage > page) {
      const xpointer = indexToXpointer(index, context);
      if (xpointer) {
        return { xpointer, percent };
      }
    }
  }

  // Последний fallback: последний элемент
  const xpointer = indexToXpointer(indices[indices.length - 1], context);
  if (xpointer) {
    return { xpointer, percent };
  }
  return null;
}

export function indexToPage(
  index: Index,
  context: RenderingContext
): Page | null {
  if (!context.layout) return null;
  const location = context.layout.elements[index];
  if (!location) return null;

  return location.startPage;
}

export function getXpointersOnPage(
  page: Page,
  context: RenderingContext
): Xpointer[] {
  if (!context.layout) return [];
  const indicesOnPage = Object.keys(context.layout.elements)
    .map(k => parseInt(k, 10))
    .filter(index => context.layout.elements[index].startPage === page)
    .sort(
      (a, b) => context.layout.elements[a].x - context.layout.elements[b].x
    );

  return indicesOnPage
    .map(index => indexToXpointer(index, context))
    .filter((xpointer): xpointer is Xpointer => xpointer !== null);
}
